import type { RichTextContent } from '../../types/contentstack'
import type { TableFields } from './types'

const text = (value: string, marks: { bold?: boolean; italic?: boolean } = {}) => ({
  text: value,
  ...marks,
})

const link = (uid: string, url: string, label: string) => ({
  type: 'a',
  uid,
  attrs: { url },
  children: [text(label)],
})

const paragraph = (uid: string, children: unknown[]) => ({
  type: 'p',
  uid,
  attrs: {},
  children,
})

const listItem = (uid: string, value: string) => ({
  type: 'li',
  uid,
  attrs: {},
  children: [paragraph(`${uid}-p`, [text(value)])],
})

const bulletList = (uid: string, items: Array<[string, string]>) => ({
  type: 'ul',
  uid,
  attrs: {},
  children: items.map(([itemUid, value]) => listItem(itemUid, value)),
})

// Mirrors the copy of the live hardcoded "How much does Gousto cost?" section —
// paragraphs, a bullet list and inline links — so the block's rich-text handling
// is exercised against the shape it will actually receive from Contentstack.
const costBody: RichTextContent = {
  type: 'doc',
  uid: 'table-cost-doc',
  attrs: {},
  children: [
    paragraph('table-cost-p1', [text('The price of your recipe box will depend on:')]),
    bulletList('table-cost-ul', [
      ['table-cost-li1', 'How many recipes you choose for the week'],
      ['table-cost-li2', 'Whether you want those meals for 1 person, 2 people, or up to 5 people'],
    ]),
    paragraph('table-cost-p2', [
      text('The more recipes that you order, the '),
      text('less you’ll pay per portion', { bold: true }),
      text('. For more information, visit our '),
      link('table-cost-link1', '/box-prices', 'box prices page'),
      text('.'),
    ]),
    paragraph('table-cost-p3', [
      text('Learn more on '),
      link('table-cost-link2', '/how-gousto-food-boxes-work', 'how Gousto recipe boxes work'),
      text(' to get the most out of your weekly meal planning.'),
    ]),
  ],
} as unknown as RichTextContent

const columns = [{ header: 'Box Size' }, { header: 'Great for' }, { header: 'First box price' }]

const rows = [
  {
    row_id: 'one',
    cells: [
      { value: '1 person box', is_bold: true },
      { value: 'Single person households' },
      { value: 'Get 5 meals for the week for just £17.50' },
    ],
  },
  {
    row_id: 'two',
    cells: [
      { value: '2 person box', is_bold: true },
      { value: '2 adults (or 1 adult + leftovers)' },
      { value: 'Get 5 meals for the week for just £22.35' },
    ],
  },
  {
    row_id: 'three',
    cells: [
      { value: '3 person box', is_bold: true },
      { value: '3 adults (or 2 adults + leftovers)' },
      { value: 'Get 5 meals for the week for just £30.00' },
    ],
  },
  {
    row_id: 'four',
    cells: [
      { value: '4 person box', is_bold: true },
      { value: '4 adults (or 2-3 adults + leftovers)' },
      { value: 'Get 5 meals for the week for just £30.88' },
    ],
  },
  {
    row_id: 'five',
    cells: [
      { value: '5 person box', is_bold: true },
      { value: '5 adults (or 3-4 adults + leftovers)' },
      { value: 'Get 3 meals for the week for just £28.38' },
    ],
  },
]

/** Full section: heading + h2 + rich-text body + CTA, table on the right. */
export const tableMockData: TableFields = {
  heading: 'How much does Gousto cost?',
  heading_level: 'h2',
  body: costBody,
  table_position: 'right',
  cta: {
    text: 'Get started',
    variant: 'primary',
    url: '/signup',
  },
  columns,
  rows,
}

/** Same section rendered at h3, for a page where the table sits under another heading. */
export const tableH3MockData: TableFields = {
  ...tableMockData,
  heading_level: 'h3',
}

/** Table on the left of the text at desktop. */
export const tableLeftMockData: TableFields = {
  ...tableMockData,
  table_position: 'left',
}

/** CTA configured to apply a promo code before redirecting. */
export const tableWithPromocodeCtaMockData: TableFields = {
  ...tableMockData,
  cta: {
    text: 'Claim your discount',
    variant: 'primary',
    url: '/signup',
    trigger_promocode: true,
  },
}

/**
 * Table-only entry — no heading, body or CTA. The block still renders its
 * layout wrapper (design.md D2: the wrapper is unconditional), so this fixture
 * guards the "no optional content" path rather than a bare-fragment render.
 */
export const tableOnlyMockData: TableFields = {
  heading_level: 'h2',
  table_position: 'right',
  columns,
  rows,
}

/** No rows — the block renders nothing at all. */
export const tableNoRowsMockData: TableFields = {
  heading: 'How much does Gousto cost?',
  heading_level: 'h2',
  table_position: 'right',
  columns,
  rows: [],
}
